import React, { useEffect, useMemo } from 'react';
import { Outlet, useLocation, useNavigate, useParams } from 'react-router';
import { useDispatch, useSelector } from 'react-redux';
import { investorActions } from '@/store/investor';
import { adminKYCApi } from '@/api';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { usePopups, useLoadingState } from '@/hooks';
import { Body3Light, BREAKPOINTS, Col, Container, Flex } from '@/ui';
import { Layout, Select, Tabs, Typography } from 'antd';
import { BackBar, Button, StatusLine } from '@/components';
import { INVESTOR_TYPES_CONFIG } from '@/utils/static';
import {
  SelectTabsInvestorAdmin,
  TabsInvestorAdmin
} from '@/components/common/organisms/AppSideBar/config';
import styled from 'styled-components';
const { Header, Content } = Layout;

export const InvestorDetails = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { showErrorToast } = usePopups();
  const investor = useSelector((state) => state.investor);

  const { refetch, isFetching } = useQuery({
    queryKey: ['fetchInvestorProfile'],
    queryFn: () => investorActions.fetchInvestorProfile(id, dispatch),
    enabled: false
  });

  useLoadingState(isFetching);

  useEffect(() => {
    if (id) refetch();
  }, [id]);

  const setInvestorStatus = async (status) => {
    if (status) {
      try {
        await adminKYCApi.setInvestorStatus({
          accountStatus: status,
          investorId: investor.id
        });

        await refetch();
      } catch (err) {
        console.error('UPDATE_COMPLIANCE_STATUS', err);
        showErrorToast(err, true);
      }
    }
  };

  const approveInvestor = async () => {
    await setInvestorStatus('ACTIVE');
  };
  const rejectInvestor = async () => {
    await setInvestorStatus('NOT_ACTIVE');
  };

  const currentTab = useMemo(() => {
    const tab = TabsInvestorAdmin.find((i) => i.href.includes(location.pathname.split('/').pop()));

    return tab ? tab.key : 0;
  }, [location]);

  const currentSelectTab = useMemo(() => {
    const tab = SelectTabsInvestorAdmin.find((i) =>
      i.href.includes(location.pathname.split('/').pop())
    );

    return tab ? tab.value : undefined;
  }, [location]);

  if (!isFetching) {
    return (
      <Layout>
        <Header>
          <Wrapper justify='space-between' align='center' gap={12}>
            <StyledRow align='center'>
              <Typography.Title level={2}>{t('Investor for approval')}</Typography.Title>
              {investor.account && (
                <StatusLine
                  statusTypes={INVESTOR_TYPES_CONFIG}
                  type={investor.account}
                  TitleStyle={Body3Light}
                  titleMargin={8}
                />
              )}
            </StyledRow>
            <Flex gap={24}>
              <Col>
                <Button
                  block={false}
                  text={investor.accountStatus === 'NOT_ACTIVE' ? t('Blocked') : t('Block')}
                  onClick={rejectInvestor}
                  disabled={investor.accountStatus === 'NOT_ACTIVE'}
                />
              </Col>
              <Col>
                <Button
                  block={false}
                  onClick={approveInvestor}
                  disabled={investor.accountStatus === 'ACTIVE'}
                  type='primary'
                  text={t('Active')}
                />
              </Col>
            </Flex>
          </Wrapper>
        </Header>

        <Content>
          <BackBar url={'/investors/PERSONAL'} />

          <Container $mdHidden $marginTop={24}>
            <Tabs
              activeKey={currentTab}
              onChange={(activeKey) => {
                navigate(TabsInvestorAdmin.find((i) => i.key === activeKey)?.href);
              }}
              items={TabsInvestorAdmin?.map((item) => {
                return { key: item.key, label: t(item.label), href: item.href };
              })}
            />
          </Container>

          <Container $hidden $mdVisible $marginTop={24}>
            <Select
              options={SelectTabsInvestorAdmin}
              style={{ width: 250 }}
              defaultValue={currentSelectTab}
              onChange={(value) => {
                navigate(SelectTabsInvestorAdmin.find((i) => i.value === value)?.href);
              }}
              size='large'
            />
          </Container>

          <Outlet />
        </Content>
      </Layout>
    );
  }

  return null;
};
const StyledRow = styled(Flex)`
  gap: 16px;

  @media (max-width: ${BREAKPOINTS.smd}px) {
    gap: 6px;
    flex-direction: column;
    align-items: flex-start;
  }
`;

const Wrapper = styled(Flex)`
  @media (max-width: ${BREAKPOINTS.smd}px) {
    flex-direction: column;
    align-items: flex-start;
  }
`;
